import type { DriverShipment } from "@khazzin/api-client";
import { formatDateTime } from "@khazzin/i18n/dates";
import { StatusChip } from "@khazzin/ui/patterns/StatusChip";
import { Field, Input } from "@khazzin/ui/primitives";
import { Button } from "@khazzin/ui/primitives/Button";
import { SegmentedControl } from "@khazzin/ui/primitives/SegmentedControl";
import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { Link, Navigate, useLocation } from "react-router";
import { useApi } from "../../api";
import { useI18n } from "../../i18n";
import { useOffline } from "../../offline/Offline";
import { timeZone } from "./Shell";
import { CustomerSection, ParcelSection } from "./StopDetail";
import { isPickup } from "./stops";
import { StopSummary } from "./Today";

/**
 * History (ADJ-1022-8b): the driver's finished deliveries or pickups, newest
 * first, by status and by the days they finished on, a page at a time, and
 * each one read-only. Online only — the phone keeps today's run and nothing
 * older — so with no signal it says so rather than failing.
 */

type Kind = "shipment" | "pickup";

/** The finished statuses of each kind, as the server lists them. */
export const HISTORY_STATUSES: Record<Kind, string[]> = {
	shipment: ["delivered", "partially_delivered", "returned", "lost", "cancelled"],
	pickup: ["collected", "received", "failed", "cancelled"],
};

export const HISTORY_PAGE = 30;

export function History() {
	const { t, locale } = useI18n();
	const { online } = useOffline();
	const api = useApi();
	const [kind, setKind] = useState<Kind>("shipment");
	const [status, setStatus] = useState("");
	const [from, setFrom] = useState("");
	const [to, setTo] = useState("");
	const badRange = from !== "" && to !== "" && to < from;
	const history = useInfiniteQuery({
		queryKey: ["driver", "history", kind, status, from, to],
		queryFn: ({ pageParam, signal }) =>
			api.listMyDriverShipmentHistory({
				kind,
				status,
				from,
				to,
				cursor: pageParam,
				limit: HISTORY_PAGE,
				signal,
			}),
		initialPageParam: "",
		getNextPageParam: (last) => last.nextCursor ?? undefined,
		enabled: online && !badRange,
	});
	// `?? []`: a list once written as null must not take the screen down.
	const rows = history.data?.pages.flatMap((p) => p.items ?? []) ?? [];

	return (
		<div className="flex flex-col gap-4">
			<h1 className="text-[18px] font-bold text-fg">{t("driver.history.title")}</h1>
			<SegmentedControl<Kind>
				aria-label={t("driver.history.kind")}
				value={kind}
				onChange={(next) => {
					setKind(next);
					setStatus("");
				}}
				options={[
					{ value: "shipment", label: t("driver.history.deliveries") },
					{ value: "pickup", label: t("driver.history.pickups") },
				]}
			/>
			<fieldset className="flex flex-wrap gap-2">
				<legend className="mb-2 text-[13px] text-fg-muted">{t("driver.history.status")}</legend>
				{["", ...HISTORY_STATUSES[kind]].map((s) => (
					<Button
						key={s}
						size="lg"
						variant={status === s ? "primary" : "secondary"}
						aria-pressed={status === s}
						onClick={() => setStatus(s)}
					>
						{t(s === "" ? "driver.history.allStatuses" : `driver.status.${s}`)}
					</Button>
				))}
			</fieldset>
			<div className="grid grid-cols-2 gap-2">
				<Field label={t("driver.history.from")}>
					{(control) => (
						<Input
							{...control}
							type="date"
							value={from}
							onChange={(e) => setFrom(e.target.value)}
						/>
					)}
				</Field>
				<Field
					label={t("driver.history.to")}
					{...(badRange ? { error: t("driver.history.badRange") } : {})}
				>
					{(control) => (
						<Input {...control} type="date" value={to} onChange={(e) => setTo(e.target.value)} />
					)}
				</Field>
			</div>

			{!online ? (
				<p role="status" className="text-[14px] text-fg-muted">
					{t("driver.history.needSignal")}
				</p>
			) : badRange ? null : history.isError ? (
				<p role="alert" className="text-[14px] text-danger-fg">
					{t("driver.history.unavailable")}
				</p>
			) : history.isPending ? (
				<p className="text-[14px] text-fg-muted">{t("driver.today.loading")}</p>
			) : rows.length === 0 ? (
				<p className="text-[14px] text-fg-muted">{t("driver.history.empty")}</p>
			) : (
				<>
					<ol className="flex flex-col gap-2">
						{rows.map((s) => (
							<li key={s.shipmentId}>
								<Link
									to={`/history/${s.shipmentId}`}
									state={s}
									className="flex min-h-12 flex-col gap-1 rounded-lg border border-border bg-bg-surface p-3"
								>
									<StopSummary stop={s} />
									{s.finishedAt && (
										<p className="text-[13px] text-fg-muted">
											{t("driver.history.finished", {
												when: formatDateTime(s.finishedAt, { locale, timeZone }),
											})}
										</p>
									)}
								</Link>
							</li>
						))}
					</ol>
					{history.hasNextPage && (
						<Button
							size="lg"
							loading={history.isFetchingNextPage}
							onClick={() => void history.fetchNextPage()}
						>
							{t("driver.history.loadMore")}
						</Button>
					)}
				</>
			)}
		</div>
	);
}

/**
 * One finished stop, read-only: the customer and the order as the stop screen
 * writes them out (name, phone, address, lines, COD), what it collected,
 * how it ended, and a delivery's proof photos. No action of any kind. The
 * row comes with the link; opened cold, it goes back to the list.
 */
export function HistoryDetail() {
	const { t, locale } = useI18n();
	const stop = useLocation().state as DriverShipment | null;
	if (stop === null || typeof stop !== "object" || !("shipmentId" in stop)) {
		return <Navigate to="/history" replace />;
	}
	return (
		<div className="flex flex-col gap-4">
			<Link to="/history" className="min-h-12 content-center text-[14px] font-bold text-brand-ink">
				{t("driver.history.back")}
			</Link>
			<header className="flex flex-col gap-1">
				<h1 className="text-[18px] font-bold text-fg">{stop.recipientName}</h1>
				<div className="flex flex-wrap gap-1.5">
					<StatusChip tone="success" size="sm">
						{t(`driver.status.${stop.status}`)}
					</StatusChip>
					{isPickup(stop) && (
						<StatusChip tone="brand" size="sm">
							{t("driver.pickup.badge")}
						</StatusChip>
					)}
				</div>
				{stop.finishedAt && (
					<p className="text-[13px] text-fg-muted">
						{t("driver.history.finished", {
							when: formatDateTime(stop.finishedAt, { locale, timeZone }),
						})}
					</p>
				)}
			</header>
			<CustomerSection stop={stop} />
			{isPickup(stop) ? (
				<section className="card p-4 text-[14px] text-fg">
					{stop.trackingNumber} · {t("driver.today.parcels", { count: stop.expectedCount ?? 1 })}
				</section>
			) : (
				<>
					<ParcelSection stop={stop} />
					<Proof shipmentId={stop.shipmentId} />
				</>
			)}
		</div>
	);
}

/** A delivery's proof photos and signature, signed for a few minutes; online only. */
function Proof({ shipmentId }: { shipmentId: string }) {
	const { t } = useI18n();
	const { online } = useOffline();
	const api = useApi();
	const proof = useQuery({
		queryKey: ["driver", "proof", shipmentId],
		queryFn: ({ signal }) => api.getShipmentProof(shipmentId, { signal }),
		enabled: online,
	});
	const files = (proof.data?.pods ?? []).flatMap((p) => [
		...(p.photos ?? []).map((f) => ({ ...f, signature: false })),
		...(p.signature ? [{ ...p.signature, signature: true }] : []),
	]);
	return (
		<section aria-label={t("driver.history.proof")} className="flex flex-col gap-2 card p-4">
			<h2 className="text-[15px] font-bold text-fg">{t("driver.history.proof")}</h2>
			{!online ? (
				<p className="text-[14px] text-fg-muted">{t("driver.history.proofOffline")}</p>
			) : proof.isError ? (
				<p className="text-[14px] text-fg-muted">{t("driver.history.proofUnavailable")}</p>
			) : proof.isPending ? (
				<p className="text-[14px] text-fg-muted">{t("driver.today.loading")}</p>
			) : files.length === 0 ? (
				<p className="text-[14px] text-fg-muted">{t("driver.history.noProof")}</p>
			) : (
				<div className="grid grid-cols-2 gap-2">
					{files.map((f, i) =>
						f.url ? (
							<img
								key={f.attachmentId}
								src={f.url}
								alt={
									f.signature
										? t("driver.history.signature")
										: t("driver.history.photo", { n: i + 1 })
								}
								className="aspect-square w-full rounded-md border border-border object-cover"
							/>
						) : (
							<p key={f.attachmentId} className="text-[13px] text-fg-muted">
								{t("driver.history.proofPending")}
							</p>
						),
					)}
				</div>
			)}
		</section>
	);
}
